import { useAppData } from '@app/core/app-data';
import { useToast } from '@app/core/notifications';
import { WatermarkConfig } from '@mi-front/core';
import { Form, NumberField, SelectField, SwitchField } from '@mi-front/ui';
import { Box, Grid, Paper } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';
import { apiClient } from '../../../../../api';
import { StationsInput } from '../../../../../components/stations/StationsInput';
import { useVideoStations } from '../../../../../core/stations/hooks/useVideoStations';
import { UploadAssetField } from '../../../../assets/screens/upload/components/UploadAssetField';

export function MediaWatermarkForm() {
  const { refetchStations } = useAppData();
  const stations = useVideoStations();
  const [station, setStation] = useState(stations[0]);
  const { $t } = useIntl();
  const { notifySuccess } = useToast();

  const positionXOptions = [
    {
      id: 'left',
      name: $t({
        id: 'admin.fragments.watermark.positionLeft',
        defaultMessage: 'Izquierda',
      }),
    },
    {
      id: 'center',
      name: $t({
        id: 'admin.fragments.watermark.positionCenter',
        defaultMessage: 'Centro',
      }),
    },
    {
      id: 'right',
      name: $t({
        id: 'admin.fragments.watermark.positionRight',
        defaultMessage: 'Derecha',
      }),
    },
  ];

  const positionYOptions = [
    {
      id: 'top',
      name: $t({
        id: 'admin.fragments.watermark.positionTop',
        defaultMessage: 'Arriba',
      }),
    },
    {
      id: 'center',
      name: $t({
        id: 'admin.fragments.watermark.positionCenter',
        defaultMessage: 'Centro',
      }),
    },
    {
      id: 'bottom',
      name: $t({
        id: 'admin.fragments.watermark.positionBottom',
        defaultMessage: 'Abajo',
      }),
    },
  ];

  const config = station.featuresConfig?.fragments?.mediaWatermark || ({} as WatermarkConfig);

  return (
    <Box>
      <Grid container>
        <Grid item md={5}>
          <StationsInput stations={stations} value={station} onChange={setStation} />
        </Grid>
      </Grid>

      <Box>
        <h6>
          {$t({
            id: 'admin.fragments.watermark.configTitle',
            defaultMessage: 'Configuración',
          })}
          :
        </h6>
        <Form
          key={station.id}
          validationSchema={yup.object().shape({
            imageUrl: yup.string().required(),
          })}
          initialValues={{
            enabled: config.enabled ?? true,
            imageUrl: config.imageUrl,
            positionX:
              positionXOptions.find((o) => o.id === config?.positionX) ?? positionXOptions[0],
            paddingX: config.paddingX ?? 10,
            positionY:
              positionYOptions.find((o) => o.id === config?.positionY) ?? positionYOptions[0],
            paddingY: config.paddingY ?? 10,
            opacity: config.opacity ?? 1,
          }}
          onSubmit={async (data) => {
            await apiClient.fragments.configUpdate(station.id, {
              mediaWatermark: {
                ...data,
                positionX: data.positionX.id,
                positionY: data.positionY.id,
              },
            });

            notifySuccess(
              $t(
                {
                  id: 'admin.fragments.fragmentsConfigUpdated',
                  defaultMessage: 'Configuración de fragmentos para {stationName} actualizada',
                },
                { stationName: station.name },
              ),
            );

            refetchStations();
          }}
        >
          {({ values, errors }) => {
            return (
              <Box mb={4}>
                <Paper>
                  <Box p={2} display={'flex'}>
                    <Box flexGrow={1}>
                      <Box>
                        <Box mb={2} display={'flex'} alignItems="top">
                          <SwitchField size={'sm'} name={'enabled'} />
                          <Box ml={1}>
                            {$t({
                              id: 'admin.fragments.watermark.active',
                              defaultMessage: 'Activo',
                            })}
                          </Box>
                        </Box>

                        <Box mb={2}>
                          <UploadAssetField
                            name={'imageUrl'}
                            inputProps={{
                              label: $t({
                                id: 'admin.fragments.watermark.loadLabel',
                                defaultMessage: 'Cargar watermark',
                              }),
                              size: 'sm',
                              accept: 'image/*',
                              maxSizeMb: 1,
                            }}
                            assetLink
                          />
                        </Box>

                        <Grid container>
                          <Grid item md={3}>
                            <SelectField
                              name="positionX"
                              label={$t({
                                id: 'admin.fragments.watermark.positionX',
                                defaultMessage: 'Posición X',
                              })}
                              options={positionXOptions}
                              getOptionValue={(m) => m.id}
                              getOptionLabel={(m) => m.name}
                              markRequired
                            />
                          </Grid>
                          <Grid item md={2} sx={{ pl: 2 }}>
                            {values.positionX?.id !== 'center' && (
                              <NumberField
                                label={$t({
                                  id: 'admin.fragments.watermark.paddingX',
                                  defaultMessage: 'Padding X',
                                })}
                                name={'paddingX'}
                              />
                            )}
                          </Grid>
                        </Grid>

                        <Grid container>
                          <Grid item md={3}>
                            <SelectField
                              name="positionY"
                              label={$t({
                                id: 'admin.fragments.watermark.positionY',
                                defaultMessage: 'Posición Y',
                              })}
                              options={positionYOptions}
                              getOptionValue={(m) => m.id}
                              getOptionLabel={(m) => m.name}
                              markRequired
                            />
                          </Grid>
                          <Grid item md={2} sx={{ pl: 2 }}>
                            {values.positionY?.id !== 'center' && (
                              <NumberField
                                label={$t({
                                  id: 'admin.fragments.watermark.paddingY',
                                  defaultMessage: 'Padding Y',
                                })}
                                name={'paddingY'}
                              />
                            )}
                          </Grid>
                        </Grid>

                        <Grid>
                          <Grid item md={2}>
                            <NumberField
                              label={$t({
                                id: 'admin.fragments.watermark.opacity',
                                defaultMessage: 'Opacidad',
                              })}
                              name={'opacity'}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            );
          }}
        </Form>
      </Box>
    </Box>
  );
}
